/**
 * The player avatar and its movement.
 *
 * Third person over first person (ADR-0005): the avatar and the kit they are
 * visibly carrying are a primary progress signal, and first person throws that
 * away. The figure itself is the shared `Humanoid` build, so the player reads
 * as one of the people in the neighbourhood rather than as a different species.
 */
import * as THREE from 'three';
import { standard } from '../world/Materials.js';
import type { Collider } from '../world/Neighbourhood.js';
import { WORLD_BOUNDS } from '../../sim/data/world.js';
import {
  buildHumanoid, HumanoidAnimator, type HumanoidParts,
} from '../world/Humanoid.js';

export const PLAYER_RADIUS = 0.5;
const WALK_SPEED = 4.6;
const RUN_SPEED = 8.6;
/**
 * Acceleration and braking are separated: reaching top speed should take a
 * moment (weight), but stopping should be quick (control). Equal values make
 * movement feel either sluggish or slippery, never responsive.
 */
const ACCELERATION = 22;
const BRAKING = 30;
const TURN_RATE = 13;

export type CarryKind = 'none' | 'mower' | 'fullkit';

export interface PlayerRig {
  group: THREE.Group;
  setCarrying(kind: CarryKind): void;
  setWorking(working: boolean): void;
  update(
    dt: number, input: MovementInput, colliders: Collider[],
    bounds?: { minX: number; maxX: number; minZ: number; maxZ: number },
  ): void;
  position: THREE.Vector3;
  facing: number;
  speed: number;
  /** Normalised 0..1 against run speed, for camera FOV and audio later. */
  speedFraction: number;
  parts: HumanoidParts;
}

export interface MovementInput {
  forward: number;
  strafe: number;
  running: boolean;
  /** Camera yaw, so movement is relative to where the player is looking. */
  cameraYaw: number;
}

export function createPlayer(x: number, z: number): PlayerRig {
  const group = new THREE.Group();
  group.position.set(x, 0, z);

  const parts = buildHumanoid({
    skin: 0xe0ab84,
    hair: 0x4a3220,
    shirt: 0x2f6f4f,
    trousers: 0x33405c,
    shoes: 0x2b2b30,
    height: 1.78,
    // The cap is the player's silhouette tell — it is how you pick yourself out
    // of a street with two dozen other people on it.
    hat: 0xd6a13a,
  });
  group.add(parts.root);
  const animator = new HumanoidAnimator(parts);

  // --- carried equipment ---------------------------------------------------
  const mower = buildMower();
  mower.position.set(0, 0, 0.72);
  mower.visible = false;
  group.add(mower);

  const backpack = new THREE.Mesh(
    new THREE.BoxGeometry(0.42, 0.5, 0.24),
    standard(0x8a5a3a, { rough: 0.9 }),
  );
  backpack.position.set(0, 1.28, -0.24);
  backpack.visible = false;
  backpack.castShadow = true;
  group.add(backpack);

  const trimmer = new THREE.Group();
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(0.035, 0.035, 1.15, 6),
    standard(0x2f3338, { rough: 0.5, metal: 0.4 }),
  );
  const guard = new THREE.Mesh(
    new THREE.CylinderGeometry(0.14, 0.14, 0.05, 10),
    standard(0xd23f2f, { rough: 0.6 }),
  );
  guard.position.y = -0.58;
  trimmer.add(shaft, guard);
  trimmer.position.set(-0.34, 1.15, -0.16);
  trimmer.rotation.z = 0.42;
  trimmer.rotation.x = -0.16;
  trimmer.visible = false;
  group.add(trimmer);

  const position = group.position;
  const velocity = new THREE.Vector3();
  let facing = 0;
  let working = false;
  let currentSpeed = 0;
  let turnRate = 0;

  function setCarrying(kind: CarryKind): void {
    mower.visible = kind !== 'none';
    backpack.visible = kind === 'fullkit';
    trimmer.visible = kind === 'fullkit';
  }

  function setWorking(value: boolean): void {
    working = value;
    mower.visible = mower.visible || value;
  }

  function update(
    dt: number, input: MovementInput, colliders: Collider[],
    // Interiors are rooms standing outside the world bounds, so while the
    // player is in one the world clamp would drag them straight back to the
    // neighbourhood. The room supplies its own.
    bounds: { minX: number; maxX: number; minZ: number; maxZ: number } = {
      minX: WORLD_BOUNDS.minX + 2, maxX: WORLD_BOUNDS.maxX - 2,
      minZ: WORLD_BOUNDS.minZ + 2, maxZ: WORLD_BOUNDS.maxZ - 2,
    },
  ): void {
    const wanted = new THREE.Vector3();
    if (!working && (input.forward !== 0 || input.strafe !== 0)) {
      // Movement is relative to the camera, which is what every third-person
      // game does and what players' hands already expect.
      const yaw = input.cameraYaw;
      const forwardVec = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
      const rightVec = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
      wanted
        .addScaledVector(forwardVec, input.forward)
        .addScaledVector(rightVec, input.strafe)
        .normalize()
        .multiplyScalar(input.running ? RUN_SPEED : WALK_SPEED);
    }

    const rate = wanted.lengthSq() > 0 ? ACCELERATION : BRAKING;
    velocity.lerp(wanted, Math.min(1, rate * dt));
    currentSpeed = velocity.length();
    if (currentSpeed < 0.05) {
      velocity.set(0, 0, 0);
      currentSpeed = 0;
    }

    // Axis-separated movement so sliding along a wall works instead of sticking.
    const nextX = position.x + velocity.x * dt;
    if (!blocked(nextX, position.z, colliders)) position.x = nextX;
    else velocity.x = 0;
    const nextZ = position.z + velocity.z * dt;
    if (!blocked(position.x, nextZ, colliders)) position.z = nextZ;
    else velocity.z = 0;

    position.x = clamp(position.x, bounds.minX, bounds.maxX);
    position.z = clamp(position.z, bounds.minZ, bounds.maxZ);

    const previousFacing = facing;
    if (currentSpeed > 0.3) {
      const target = Math.atan2(velocity.x, velocity.z);
      facing = smoothAngle(facing, target, Math.min(1, TURN_RATE * dt));
    }
    turnRate = dt > 0 ? shortestAngle(previousFacing, facing) / dt : 0;
    group.rotation.y = facing;

    animator.update(
      dt, currentSpeed, input.running, turnRate,
      working ? 'working' : 'locomotion',
    );
  }

  return {
    group, setCarrying, setWorking, update, position, parts,
    get facing() { return facing; },
    get speed() { return currentSpeed; },
    get speedFraction() { return Math.min(1, currentSpeed / RUN_SPEED); },
  };
}

function buildMower(): THREE.Group {
  const g = new THREE.Group();
  const deck = new THREE.Mesh(
    new THREE.BoxGeometry(0.58, 0.2, 0.46),
    standard(0xc23f30, { rough: 0.55 }),
  );
  deck.position.y = 0.28;
  deck.castShadow = true;
  g.add(deck);

  const engine = new THREE.Mesh(
    new THREE.BoxGeometry(0.26, 0.18, 0.24),
    standard(0x3a3d42, { rough: 0.5, metal: 0.3 }),
  );
  engine.position.set(0, 0.44, -0.02);
  engine.castShadow = true;
  g.add(engine);

  const bag = new THREE.Mesh(
    new THREE.BoxGeometry(0.38, 0.26, 0.22),
    standard(0x4a5a3a, { rough: 0.95 }),
  );
  bag.position.set(0, 0.4, -0.3);
  g.add(bag);

  const handleMat = standard(0x40444a, { rough: 0.4, metal: 0.5 });
  for (const x of [-0.22, 0.22]) {
    const bar = new THREE.Mesh(new THREE.CylinderGeometry(0.022, 0.022, 0.8, 6), handleMat);
    bar.position.set(x, 0.6, -0.42);
    bar.rotation.x = 0.62;
    g.add(bar);
  }
  const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.026, 0.026, 0.46, 6), handleMat);
  grip.position.set(0, 0.92, -0.66);
  grip.rotation.z = Math.PI / 2;
  g.add(grip);

  const wheelMat = standard(0x1e2126, { rough: 0.85 });
  for (const x of [-0.28, 0.28]) {
    for (const [z, r] of [[-0.17, 0.13], [0.19, 0.11]] as [number, number][]) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 0.07, 10), wheelMat);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(x, r, z);
      wheel.castShadow = true;
      g.add(wheel);
    }
  }
  return g;
}

function blocked(x: number, z: number, colliders: Collider[]): boolean {
  for (const c of colliders) {
    // Soft colliders exist to keep the camera out of foliage; walking into a
    // tree trunk is the player's business.
    if (c.soft) continue;
    if (
      x > c.minX - PLAYER_RADIUS && x < c.maxX + PLAYER_RADIUS &&
      z > c.minZ - PLAYER_RADIUS && z < c.maxZ + PLAYER_RADIUS
    ) return true;
  }
  return false;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function shortestAngle(from: number, to: number): number {
  let diff = ((to - from + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return diff;
}

function smoothAngle(from: number, to: number, t: number): number {
  return from + shortestAngle(from, to) * t;
}
